import { formatHumanName } from "@medplum/core";
import { useMedplum } from "@medplum/react-hooks";
import { Lettermark, Logo } from "@repo/ui/components/logo";
import { useTheme } from "@repo/ui/components/theme-provider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@repo/ui/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@repo/ui/components/ui/sidebar";
import { Link, useMatchRoute, useRouter } from "@tanstack/react-router";
import { ChevronsUpDown, LogOut, Settings } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export interface AppRoute {
  title: string;
  url: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function NavUser() {
  const { isMobile } = useSidebar();
  const { theme, setTheme } = useTheme();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const medplum = useMedplum();
  const router = useRouter();

  const profile = medplum.getProfile();
  const name = profile?.name?.[0] ? formatHumanName(profile.name[0]) || "User" : "User";
  const email = profile?.telecom?.find((t) => t.system === "email")?.value || "";
  const initials = getInitials(name);

  const handleLogout = async () => {
    await medplum.signOut();
    setShowLogoutDialog(false);
    await router.invalidate();
    window.location.href = "/login";
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center"
                />
              }
            >
              <Avatar className="h-8 w-8 rounded-lg shrink-0">
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{name}</span>
                <span className="truncate text-xs">{email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[var(--anchor-width)] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{name}</span>
                      <span className="truncate text-xs">{email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Toggle Theme</DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => setTheme("light")}>
                        <span
                          className={cn("size-2 rounded-full", theme === "light" ? "bg-primary" : "bg-transparent")}
                        />
                        Light
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("dark")}>
                        <span
                          className={cn("size-2 rounded-full", theme === "dark" ? "bg-primary" : "bg-transparent")}
                        />
                        Dark
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("system")}>
                        <span
                          className={cn("size-2 rounded-full", theme === "system" ? "bg-primary" : "bg-transparent")}
                        />
                        System
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setShowLogoutDialog(true)}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>You will be redirected to the login page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>Log out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  routes: AppRoute[];
}

export function AppSidebar({ routes, ...props }: AppSidebarProps) {
  const matchRoute = useMatchRoute();

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      {/*
        Brand sits above the nav and shares its optical grid: expanded, the
        lockup starts on the same left edge as the nav icons; collapsed, the
        mark is centred in a box the size of a nav button, so it lands on the
        same axis as the icon column.
      */}
      <SidebarHeader className="items-start px-4 pt-3 pb-1 group-data-[collapsible=icon]:px-2">
        <Logo variant="adaptive" className="h-6 group-data-[collapsible=icon]:hidden" title={null} />
        {/* size-8 + the nav group's 1px inset reproduces a nav button's box exactly. */}
        <div className="ml-px hidden size-8 items-center justify-center group-data-[collapsible=icon]:flex">
          <Lettermark variant="adaptive" className="w-7" title={null} />
        </div>
        <span className="sr-only">MindForge</span>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent className="px-1.5 md:px-0 pt-3">
            <SidebarMenu className="gap-2">
              {routes.map((item) => {
                const isActive = !!matchRoute({ to: item.url, fuzzy: true });
                return (
                  <SidebarMenuItem key={item.title}>
                    <Link to={item.url}>
                      <SidebarMenuButton
                        tooltip={{
                          children: item.title,
                          hidden: false,
                        }}
                        isActive={isActive}
                        className="px-2.5 md:px-2"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to="/settings">
              <SidebarMenuButton
                tooltip={{
                  children: "Settings",
                  hidden: false,
                }}
                isActive={!!matchRoute({ to: "/settings", fuzzy: true })}
                className="px-2.5 md:px-2"
              >
                <Settings />
                <span>Settings</span>
              </SidebarMenuButton>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
